import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";

import { ds } from "../../styles/designSystem";
import { useMyPermissions } from "../../util/useMyPermissions";
import { notify } from "../../util/toast";
import { confirm } from "../../util/confirmDialog";
import ActionButton from "../../component/common/ActionButton";
import LoadingDots from "../../component/common/LoadingDots";

import { feedbackApi } from "./api/feedbackApi";
import { statusMeta, FEEDBACK_STATUS_ORDER } from "./feedbackConstants";
import FeedbackCard from "./FeedbackCard";
import FeedbackComposeModal from "./FeedbackComposeModal";
import FeedbackDetailModal from "./FeedbackDetailModal";

const C = ds.colors;
const PAGE_SIZE = 10;

const FILTERS = [
    { key: "ALL", label: "전체" },
    ...FEEDBACK_STATUS_ORDER.map((s) => ({ key: s, label: statusMeta(s).label })),
];

/**
 * 피드백 게시판 페이지.
 *
 * 익명 정책은 서버가 응답 단계에서 처리(작성자 마스킹). 이 페이지는 응답의 author 유무/ mine 플래그로만 표시를 분기한다.
 * 상태 변경은 관리자(GLOBAL_ADMIN)만 가능하며, 완료 시 작성자에게 메일이 발송된다.
 */
export default function FeedbackPage() {
    const history = useHistory();
    const location = useLocation();
    const { isAdmin } = useMyPermissions();

    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [total, setTotal] = useState(0);
    const [filter, setFilter] = useState("ALL");

    const [composeOpen, setComposeOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [detail, setDetail] = useState({ open: false, id: null, item: null });

    const sentinelRef = useRef(null);

    const buildParams = (pageNum) => {
        const params = { page: pageNum, size: PAGE_SIZE };
        if (filter !== "ALL") params.status = filter;
        return params;
    };

    const load = useCallback(async ({ showSpinner = true } = {}) => {
        if (showSpinner) setLoading(true);
        try {
            const data = await feedbackApi.list(buildParams(0));
            const payload = data?.data ?? data;
            setFeeds(Array.isArray(payload?.feeds) ? payload.feeds : []);
            setHasNext(Boolean(payload?.hasNext));
            setTotal(payload?.totalElements ?? 0);
            setPage(0);
        } catch (e) {
            notify(e?.message || "피드백을 불러오지 못했습니다.", "error");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const loadMore = useCallback(async () => {
        if (loadingMore || !hasNext) return;
        setLoadingMore(true);
        try {
            const next = page + 1;
            const data = await feedbackApi.list(buildParams(next));
            const payload = data?.data ?? data;
            const more = Array.isArray(payload?.feeds) ? payload.feeds : [];
            setFeeds((prev) => {
                const ids = new Set(prev.map((f) => f.id));
                return [...prev, ...more.filter((f) => !ids.has(f.id))];
            });
            setHasNext(Boolean(payload?.hasNext));
            setPage(next);
        } catch (e) {
            notify(e?.message || "더 불러오지 못했습니다.", "error");
        } finally {
            setLoadingMore(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingMore, hasNext, page, filter]);

    // 변경 후 목록 갱신 — 현재까지 불러온 페이지(0..page)를 한 번에 재조회한다.
    // page 0 으로 리셋하면 스크롤로 쌓인 카드가 접히고 방금 처리한 항목이 사라지므로,
    // 로드된 윈도우 전체(size = (page+1)*PAGE_SIZE)를 다시 받아 위치/개수를 보존한다.
    const silentRefresh = useCallback(async () => {
        try {
            const size = Math.max(PAGE_SIZE, (page + 1) * PAGE_SIZE);
            const params = filter !== "ALL"
                ? { status: filter, page: 0, size }
                : { page: 0, size };
            const data = await feedbackApi.list(params);
            const payload = data?.data ?? data;
            setFeeds(Array.isArray(payload?.feeds) ? payload.feeds : []);
            setHasNext(Boolean(payload?.hasNext));
            setTotal(payload?.totalElements ?? 0);
            // page 는 유지 (윈도우 크기 보존)
        } catch (e) {
            notify(e?.message || "목록을 갱신하지 못했습니다.", "error");
        }
    }, [page, filter]);

    // 필터 변경 시 리로드
    useEffect(() => { load({ showSpinner: true }); }, [load]);

    // 무한 스크롤
    useEffect(() => {
        if (!sentinelRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0].isIntersecting) loadMore(); },
            { threshold: 0.1 }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [loadMore]);

    // 알림/deep link 로 진입 시 ?openFeedback=<id> 자동 열기 (한 번만).
    // 별도 prefetch 없이 id 만 넘겨 상세 모달이 단일 조회하도록 한다(중복 조회 방지).
    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const id = sp.get("openFeedback");
        if (!id) return;
        setDetail({ open: true, id: Number(id), item: null });
        history.replace(location.pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // ── 핸들러 ────────────────────────────────────────────
    const openCompose = () => { setEditTarget(null); setComposeOpen(true); };
    const openEdit = (item) => {
        setDetail({ open: false, id: null, item: null });
        setEditTarget(item);
        setComposeOpen(true);
    };
    const openDetail = (item) => setDetail({ open: true, id: item.id, item });
    const closeDetail = () => setDetail({ open: false, id: null, item: null });

    const deleteFeedback = async (item) => {
        if (!(await confirm({ message: "이 피드백을 삭제할까요?", variant: "danger", confirmLabel: "삭제" }))) return;
        try {
            await feedbackApi.remove(item.id);
            notify("삭제되었습니다.", "success");
            silentRefresh();
        } catch (e) {
            notify(e?.message || "삭제에 실패했습니다.", "error");
        }
    };

    const changeStatus = async (item, next) => {
        if (next === item.status) return;
        if (next === "DONE") {
            const ok = await confirm({
                title: "완료 처리",
                message: "완료로 변경하면 작성자에게 완료 메일이 발송됩니다.\n진행할까요?",
            });
            if (!ok) return;
        }
        try {
            await feedbackApi.updateStatus(item.id, next);
            notify("상태가 변경되었습니다.", "success");
            silentRefresh();
        } catch (e) {
            notify(e?.message || "상태 변경에 실패했습니다.", "error");
        }
    };

    const toggleLike = async (item) => {
        // 낙관적 업데이트 후 서버 응답으로 확정
        setFeeds((prev) => prev.map((f) => f.id === item.id
            ? { ...f, likedByMe: !f.likedByMe, likeCount: Math.max(0, (f.likeCount ?? 0) + (f.likedByMe ? -1 : 1)) }
            : f));
        try {
            const res = await feedbackApi.toggleLike(item.id);
            const d = res?.data ?? res;
            setFeeds((prev) => prev.map((f) => f.id === item.id
                ? { ...f, likedByMe: d.liked, likeCount: d.likeCount }
                : f));
        } catch (e) {
            notify(e?.message || "좋아요 처리에 실패했습니다.", "error");
            silentRefresh();
        }
    };

    return (
        <Box sx={{ maxWidth: 820, mx: "auto", pb: 8, py: 4 }}>
            {/* 헤더 */}
            <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 3 }}>
                <Box>
                    <Typography sx={{ ...ds.typography.pageTitle }}>피드백</Typography>
                    <Typography sx={{ ...ds.typography.pageSubtitle, mt: 0.3 }}>
                        전 직원이 자유롭게 의견을 남기는 공간 · 전체 {total}건
                    </Typography>
                </Box>
                <ActionButton variant="add" label="새 글 작성" onClick={openCompose} />
            </Stack>

            {/* 상태 필터 */}
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1 }}>
                {FILTERS.map((f) => {
                    const active = filter === f.key;
                    return (
                        <Chip
                            key={f.key}
                            label={f.label}
                            onClick={() => setFilter(f.key)}
                            sx={{
                                fontWeight: 600,
                                fontSize: 13,
                                cursor: "pointer",
                                bgcolor: active ? C.primary : "#fff",
                                color: active ? "#fff" : C.textSecondary,
                                border: `1px solid ${active ? C.primary : C.border}`,
                                "&:hover": { bgcolor: active ? C.primaryDark : C.primaryBg },
                            }}
                        />
                    );
                })}
            </Stack>

            {/* 목록 */}
            <Box
                sx={{
                    bgcolor: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 1.5,
                    overflow: "hidden",
                }}
            >
                {loading ? (
                    <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                        <LoadingDots label="불러오는 중" />
                    </Box>
                ) : feeds.length === 0 ? (
                    <Box sx={{ py: 8, textAlign: "center" }}>
                        <Typography sx={{ fontSize: 14, color: C.textMuted }}>
                            아직 등록된 피드백이 없습니다. 첫 의견을 남겨보세요.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {feeds.map((item) => (
                            <FeedbackCard
                                key={item.id}
                                item={item}
                                isAdmin={isAdmin}
                                onOpenDetail={openDetail}
                                onEdit={openEdit}
                                onDelete={deleteFeedback}
                                onStatusChange={changeStatus}
                                onToggleLike={toggleLike}
                                onRefresh={silentRefresh}
                            />
                        ))}
                        {/* 무한 스크롤 sentinel + 폴백 버튼 */}
                        <Box ref={sentinelRef} sx={{ py: hasNext ? 2 : 0, display: "flex", justifyContent: "center" }}>
                            {loadingMore && <LoadingDots compact />}
                            {hasNext && !loadingMore && (
                                <ActionButton variant="secondary" label="더 보기" onClick={loadMore} />
                            )}
                        </Box>
                    </>
                )}
            </Box>

            {/* 작성/수정 모달 */}
            <FeedbackComposeModal
                open={composeOpen}
                editTarget={editTarget}
                onClose={() => setComposeOpen(false)}
                onSaved={silentRefresh}
            />

            {/* 상세 모달 */}
            <FeedbackDetailModal
                open={detail.open}
                feedbackId={detail.id}
                initialItem={detail.item}
                isAdmin={isAdmin}
                onClose={closeDetail}
                onChanged={silentRefresh}
                onEdit={openEdit}
            />
        </Box>
    );
}
